import { hasExpired, measureClockLead } from './tokenExpiry'

export { useSessionToken } from './tokenMethods'

export interface TokenRequest {
  /** Forces Clerk to mint a new token, e.g. to retry a request the server rejected with 401. */
  skipCache?: boolean
}
export type TokenProvider = (options?: TokenRequest) => Promise<string | null>
export type TokenErrorReporter = (error: unknown) => void

let provider: TokenProvider | null = null
let reportError: TokenErrorReporter | undefined

/** Registers the token source (Clerk's `getToken`) and where its failures go; pass `null` on teardown. */
export const setTokenProvider = (
  next: TokenProvider | null,
  onError?: TokenErrorReporter,
): void => {
  provider = next
  reportError = onError
}

const mint = async (current: TokenProvider): Promise<string | null> => {
  const token = await current({ skipCache: true })
  if (token) measureClockLead(token)
  return token
}

let renewal: Promise<string | null> | null = null

const renewOnce = (current: TokenProvider): Promise<string | null> => {
  renewal ??= mint(current).finally(() => {
    renewal = null
  })
  return renewal
}

/**
 * The current session token, asked of Clerk on every call. Clerk caches the token and refreshes it
 * before it expires, so the app keeps no copy of its own: when Clerk cannot produce a token this
 * reports why and returns undefined, never an older token that may have expired.
 *
 * A tab back from idle can get Clerk's last token after it expired, while Clerk rebuilds the
 * session; such a token is replaced with a new one, minted once for all requests waiting on it.
 */
export const getValidToken = async (options?: TokenRequest): Promise<string | undefined> => {
  if (!provider) return undefined
  try {
    if (options?.skipCache) return (await mint(provider)) ?? undefined
    const cached = await provider(options)
    if (!cached || !hasExpired(cached)) return cached ?? undefined
    return (await renewOnce(provider)) ?? undefined
  } catch (error) {
    reportError?.(error)
    return undefined
  }
}

/**
 * The session token an authenticated request must carry. Clerk can briefly fail to produce one,
 * typically while a laptop that just woke is still offline, so a miss is retried once: when the
 * browser is back online, or right away if it never went offline. A second miss throws
 * {@link MissingSessionTokenError}, and the caller sends nothing rather than an unauthenticated
 * request.
 */
export const requireSessionToken = async (options?: TokenRequest): Promise<string> => {
  const token = (await getValidToken(options)) ?? (await retryOnceOnline(options))
  if (!token) throw new MissingSessionTokenError()
  return token
}

/** Clerk has no session token, so an authenticated request is not sent: the gateway would reject it. */
export class MissingSessionTokenError extends Error {
  constructor() {
    super('No Clerk session token; the request was not sent')
    this.name = 'MissingSessionTokenError'
  }
}

const ONLINE_WAIT_MS = 15_000

const retryOnceOnline = async (options?: TokenRequest): Promise<string | undefined> => {
  await waitUntilOnline()
  return getValidToken(options)
}

/**
 * Resolves when the browser reports a connection, or after at most 15s. Only this wait is bounded:
 * Clerk's own token fetch, before and after it, has no deadline here.
 * Outside a browser (no `window`, or no `navigator.onLine` as in Node) there is nothing to wait for.
 */
const waitUntilOnline = (): Promise<void> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return Promise.resolve()
  if (navigator.onLine !== false) return Promise.resolve()
  return new Promise(resolve => {
    const done = () => {
      clearTimeout(timer)
      window.removeEventListener('online', done)
      resolve()
    }
    const timer = setTimeout(done, ONLINE_WAIT_MS)
    window.addEventListener('online', done)
  })
}
